import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch, Ref} from 'vue-property-decorator';
import { APP_EVENT_BUS } from '@/core/enums/app.enum';
import {CLASSES_STORE} from '@/classes/store/classes.store';
import Organization from '@/tools/models/organization.model';
import {STUDENT_CLICK_EVENTS} from '@/tools/values/student.values';
import EdStudentListClassComponent from '@/tools/components/ed-student-admin/ed-student-list-class/ed-student-list-class.component.vue';
import EdInputModel from '@/models/ed-input.model';


@Component({
    name: 'ed-student-list',
    components: {
        EdStudentListClass: EdStudentListClassComponent
    }
})
export default class EdStudentListComponent extends Vue {

    /* Props */
    @Prop() readonly organization: Organization;

    @Ref('dataTable') readonly dataTable: any;

    /* Data */
    public loading: boolean = false
    public students: any = []
    public pagination: object = { current: 1, total: 0 }
    public expanded: any = []
    public prevExpanded: any = []

    //-- Filters
    public classes: any = []
    public filterClass: any = []
    public filterPerson = EdInputModel('filterPerson',  '');

    /* Computed */
    get headers() {
        return [
            { text: this.$t('classes.headers.username'), align: 'start', value: 'userId', sortable: true },
            { text: this.$t('tools.student.headers.name'), value: 'firstName', sortable: true },
            { text: this.$t('tools.student.headers.lastName'), value: 'lastName', sortable: true },
            { text: this.$t('tools.student.headers.email'), value: 'email', sortable: true },
            { text: this.$t('tools.student.headers.classes'), value: 'classes', sortable: false },
        ]
    }

    get expandedItem(): any {
        return this.expanded?.length > 0 ? this.expanded[0] : undefined;
    }

    get studentsList(): any[] {
        return this.students;
    }

    /* Methods */
    public calcName(name:string){
        if (name){
            if (name.length>20){
                return name.substring(0, 20) + ' ...';
            }else{
               return name;
            }
        }else{
            return '';
        }
    }

    public resetFilter(){
        this.filterClass = [];
        this.filterPerson.value = '';
        this.expanded = [];
        this.prevExpanded = {};
        this.students = [];
        this.pagination =  { current: 1, total: 0 };
        this.fetchStudents();
    }

    clickColumn(slotData: any) {
        if (this.prevExpanded.header &&  (slotData.header.value === this.prevExpanded.header.value) && slotData.index === this.prevExpanded.index) {
            this.expanded = []
            this.prevExpanded = {};
        } else {
            this.expanded = [slotData.item]
            this.prevExpanded = slotData;
        }
        this._loadStudentsListInDataTable();
    }

    fetchStudents(){
        this.loading = true;
        if (this.organization['organizationId']) {
            this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_ORGANIZATION_CLASS_STUDENTS_A}`, {
                orgId: this.organization['organizationId'],
                page: this.pagination['current'] - 1,
                pageSize: 10,
                classIds: (this.filterClass??'').join(),
                personFilter: this.filterPerson.value
            }).then((res) => {
                this.students = res.content;
                this.pagination['total'] = res.totalPages;
                this.loading = false;
            }).catch(() => {
                this.pagination = {current: 1, total: 0};
                this.students = [];
                this.loading = false;
            })
        }else{
            this.loading = false;
        }
    }

    private clickStudent(item:any) : void {
        this.$edEventBus.$emit(STUDENT_CLICK_EVENTS.SHOW_STUDENT, {item});
    }

    public isItemExpanded(item: any): boolean {
        return this.expandedItem?.id === item?.id;
    }

    public onPageUpdate(customEvent: CustomEvent): void {
        const detail: { page: number } = customEvent.detail;
        this.pagination['current'] = detail.page;
        this.fetchStudents();
    }

    private _onLanguageChanged(lang: string): void {
        this.$nextTick(() => {
            this.dataTable?.setAttribute('lang', lang);
            this._loadStudentsListInDataTable();
        });
    }

    @Watch('studentsList', { deep: true, immediate: true })
    private _watchStudentsList(): void {
        this._loadStudentsListInDataTable();
    }

    private _loadStudentsListInDataTable(): void {

        if (!this.dataTable || !this.dataTable.loadData) {
            return;
        }

        if (!this.studentsList || this.studentsList.length == 0) {
            this.dataTable.loadData({ items: [], headers: this.headers });
            return;
        }

        const slots = {
            'item.data-table-expand': { template: '' },
            'item.userId': {
                template: document.querySelector('#item\\.userId').innerHTML,
                templateParams: [{
                    key: 'item.userId',
                    value: (params) => params.item.userId,
                }],
                events: [
                    { target: '.userId', handler: (params) => params.parent.clickStudent(params.item) },
                ],
            },
            'item.classes': {
                template: document.querySelector('#item\\.classes').innerHTML,
                templateParams: [{}],
                expressions: [
                    {
                      target: '.showStudentClasses', attribute: 'style', booleanAttribute: false,
                      expression: (params) => !params.parent.isItemExpanded(params.item),
                      applied: 'display: inline; pointer-events: none;', fallback: 'display: none;'
                    },
                    { 
                      target: '.hideStudentClasses', attribute: 'style', booleanAttribute: false,
                      expression: (params) => params.parent.isItemExpanded(params.item),
                      applied: 'display: inline; pointer-events: none;', fallback: 'display: none;'
                    },
                ],
                events: [
                    { target: '.studentClasses', handler: (params) => params.parent.clickColumn(params.slotData) },
                ],
            },
            parent: this,
        };

        this.dataTable.loadData({
            items: this.studentsList,
            headers: this.headers,
            slots: slots,
            expanded: this.expanded,
        });
    }

    /* Hooks */
    created() {
        this.fetchClasses();
        this.fetchStudents();
    }

    /**
     * Called after the component has updated its DOM tree.
     * Inside it, we're calling the DataTable's load method in order to guarantee it's updating;
     * The above covers scenarios where language change event was not notified (handled with APP_EVENT_BUS.LANGUAGE_CHANGED)
     */
    updated() {
        this._loadStudentsListInDataTable();
    }

    fetchClasses(){
        //-- Load classes by Org
        this.$store.dispatch(`${CLASSES_STORE.ACTIONS.GET_CLASS_BY_ORG_A}`, {
            orgId: this.organization['organizationId'],
            userId: null,
            page: 0,
            pageSize: -1
        }).then((res) => {
            this.classes = [{id:-1, name: this.$t('tools.student.unassigned')}];
            this.classes.push(...res.content);
            this.loading = false;
        }).catch((e) => {
            this.classes = [];
            this.loading = false;
        })
    }

    /* Watch */

    @Watch('filterPerson.value')
    searchStudents(value: string): void {
        if (!this.loading || value == null || value === '') {
            this.pagination['current'] = 1;
            this.fetchStudents();
        }
    }

    @Watch('organization', { immediate: true, deep: true })
    onChangeInstitution() {
        this.pagination['current'] = 1;
        this.fetchClasses();
        this.fetchStudents();
    }

}
